import React, { useState, useEffect } from 'react';
import CompanyBranding from '../components/CompanyBranding';
import Navbar from '../components/Navbar';
import ThingsBoardSettings from '../components/ThingsBoardSettings';
import UserManagement from './UserManagement';
import { userService, dashboardService } from '../services';
import api from '../services/api';
import { Users, LayoutDashboard, Plus, Edit2, Trash2, Link as LinkIcon, Settings as SettingsIcon, UserCog, Building2 } from 'lucide-react';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('user-management');
  const [users, setUsers] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [companies, setCompanies] = useState([]);

  const [userForm, setUserForm] = useState({
    company_id: '',
    username: '',
    email: '',
    password: '',
  });

  const [assignForm, setAssignForm] = useState({
    user_id: '',
    device_id: '',
    thingsboard_device_id: ''
  });

  const companyName = (id) => {
    const match = companies.find((c) => String(c.id) === String(id));
    return match ? match.name : 'Unknown';
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // The company picker on the create-user form needs these, whichever tab is open.
  useEffect(() => {
    api.get('/branding/companies')
      .then((res) => setCompanies(res.data))
      .catch((e) => console.error('Failed to load companies:', e));
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const usersData = await userService.getAll();
        setUsers(usersData);
      } else {
        const dashboardsData = await dashboardService.getAll();
        setDashboards(dashboardsData);
      }
    } catch (error) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      await userService.create(userForm);
      setSuccess('User created successfully');
      setShowUserModal(false);
      setUserForm({
        username: '',
        email: '',
        password: '',
      });
      loadData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await userService.delete(userId);
      setSuccess('User deleted successfully');
      loadData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleAssignDashboard = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      await dashboardService.assignToUser(selectedDashboard.dashboard_id, assignForm);
      setSuccess('Dashboard assigned successfully');
      setShowAssignModal(false);
      setAssignForm({
        user_id: '',
        device_id: '',
        thingsboard_device_id: ''
      });
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to assign dashboard');
    }
  };

  const openAssignModal = (dashboard) => {
    setSelectedDashboard(dashboard);
    setShowAssignModal(true);
  };

  return (
    <div className="admin-page">
      <Navbar />
      
      <div className="admin-container">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <p>Manage users, dashboards, and assignments</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="admin-tabs">
          <button
            className={`tab-btn ${activeTab === 'user-management' ? 'active' : ''}`}
            onClick={() => setActiveTab('user-management')}
          >
            <UserCog size={18} />
            User Management
          </button>
          <button
            className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={18} />
            Users
          </button>
          <button
            className={`tab-btn ${activeTab === 'dashboards' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboards')}
          >
            <LayoutDashboard size={18} />
            Dashboards
          </button>
          <button
            className={`tab-btn ${activeTab === 'companies' ? 'active' : ''}`}
            onClick={() => setActiveTab('companies')}
          >
            <Building2 size={18} />
            Companies
          </button>
          <button
            className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <SettingsIcon size={18} />
            ThingsBoard Settings
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'user-management' && <UserManagement />}

          {activeTab === 'companies' && <CompanyBranding />}

          {activeTab === 'users' && (
            <div className="users-section">
              <div className="section-header">
                <h2>Users Management</h2>
                <button className="btn btn-primary" onClick={() => setShowUserModal(true)}>
                  <Plus size={18} />
                  Add User
                </button>
              </div>

              {loading ? (
                <div className="loading">Loading users...</div>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Company</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td>{user.id}</td>
                          <td>{user.username}</td>
                          <td>{user.email}</td>
                          <td>
                            <span className={`badge badge-${user.role}`}>
                              {user.role}
                            </span>
                          </td>
                          <td>
                            {user.company_id ? (
                              <span className="status-connected">{companyName(user.company_id)}</span>
                            ) : (
                              <span className="status-disconnected">None</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-icon btn-danger"
                              onClick={() => handleDeleteUser(user.id)}
                              title="Delete User"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'dashboards' && (
            <div className="dashboards-section">
              <div className="section-header">
                <h2>Dashboards Management</h2>
              </div>

              {loading ? (
                <div className="loading">Loading dashboards...</div>
              ) : (
                <div className="dashboards-grid">
                  {dashboards.map((dashboard) => (
                    <div key={dashboard.id} className="dashboard-card">
                      <div className="dashboard-card-header">
                        <h3>{dashboard.name}</h3>
                        <span className="dashboard-id">{dashboard.dashboard_id}</span>
                      </div>
                      <p className="dashboard-description">{dashboard.description}</p>
                      <div className="dashboard-card-footer">
                        <span className="dashboard-type">{dashboard.type}</span>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => openAssignModal(dashboard)}
                        >
                          <LinkIcon size={14} />
                          Assign to User
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>ThingsBoard Configuration</h2>
              </div>
              <ThingsBoardSettings />
            </div>
          )}
        </div>

        {/* Create User Modal */}
        {showUserModal && (
          <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Create New User</h2>
                <button className="modal-close" onClick={() => setShowUserModal(false)}>
                  &times;
                </button>
              </div>
              <form onSubmit={handleCreateUser} className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      value={userForm.username}
                      onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    required
                  />
                </div>

                <hr />

                <div className="form-group">
                  <label htmlFor="new-user-company">Company *</label>
                  <select
                    id="new-user-company"
                    value={userForm.company_id}
                    onChange={(e) => setUserForm({ ...userForm, company_id: e.target.value })}
                    required={userForm.role !== 'platform_admin'}
                  >
                    <option value="">
                      {userForm.role === 'platform_admin' ? 'None (PrimeHive staff)' : 'Choose a company...'}
                    </option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <span className="form-help">
                    Decides the name and logo this person sees, and which machines they can reach.
                  </span>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowUserModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Assign Dashboard Modal */}
        {showAssignModal && selectedDashboard && (
          <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Assign Dashboard: {selectedDashboard.name}</h2>
                <button className="modal-close" onClick={() => setShowAssignModal(false)}>
                  &times;
                </button>
              </div>
              <form onSubmit={handleAssignDashboard} className="modal-body">
                <div className="form-group">
                  <label>Select User *</label>
                  <select
                    value={assignForm.user_id}
                    onChange={(e) => setAssignForm({ ...assignForm, user_id: e.target.value })}
                    required
                  >
                    <option value="">Choose a user...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.username} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Device ID (Optional)</label>
                  <input
                    type="text"
                    value={assignForm.device_id}
                    onChange={(e) => setAssignForm({ ...assignForm, device_id: e.target.value })}
                    placeholder="e.g., device001"
                  />
                </div>

                <div className="form-group">
                  <label>ThingsBoard Device ID</label>
                  <input
                    type="text"
                    value={assignForm.thingsboard_device_id}
                    onChange={(e) => setAssignForm({ ...assignForm, thingsboard_device_id: e.target.value })}
                    placeholder="ThingsBoard device UUID"
                  />
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Assign Dashboard
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
